import React, { useMemo, useState } from "react";
import { useVoteData } from "../../context/VoteDataContext";
import { useSettings } from "../../context/SettingsContext";
import { useHandicap } from "../../context/HandicapContext";
import aflPlayerData from "../../assets/afl_player_data_2026.json";
import DetailedTeamVotesModal from "./DetailedTeamVotesModal";
import DetailedPlayerVoteModal from "../Dashboard/DetailedPlayerVoteModal";
import BreakdownIcon from "../icons/BreakdownIcon";
import { deadHeatShare, formatWins, formatPct } from "../../utils/deadHeat";
import ImpliedOddsInfo from "../common/ImpliedOddsInfo";
import AdlLogo from "../../assets/team_logos/adl.svg";

import BlLogo from "../../assets/team_logos/bl.svg";
import CarlLogo from "../../assets/team_logos/carl.svg";
import CollLogo from "../../assets/team_logos/coll.svg";
import EssLogo from "../../assets/team_logos/ess.svg";
import FreLogo from "../../assets/team_logos/fre.svg";
import GeelLogo from "../../assets/team_logos/geel.svg";
import GcLogo from "../../assets/team_logos/gc.svg";
import GwsLogo from "../../assets/team_logos/gws.svg";
import HawLogo from "../../assets/team_logos/haw.svg";
import MelbLogo from "../../assets/team_logos/melb.svg";
import NmLogo from "../../assets/team_logos/nm.svg";
import PortLogo from "../../assets/team_logos/port.svg";
import RichLogo from "../../assets/team_logos/rich.svg";
import StkLogo from "../../assets/team_logos/stk.svg";
import SydLogo from "../../assets/team_logos/syd.svg";
import WceLogo from "../../assets/team_logos/wce.svg";
import WbdLogo from "../../assets/team_logos/wbd.svg";

const normalize = (value) =>
    (value || "")
        .toString()
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\s+/g, " ")
        .trim();

// Caret shown in a sortable header when that column is the active sort key.
const SortCaret = () => (
    <svg className="inline-block w-2.5 h-2.5 ml-1 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" />
    </svg>
);

// Marks a per-source cell where the player/team leads that dataset.
const LeaderCheck = ({ className = 'w-2.5 h-2.5 mt-0.5' }) => (
    <svg className={`${className} text-emerald-400`} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
    </svg>
);

const ChevronDown = ({ className = 'w-3.5 h-3.5' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
    </svg>
);

const SELECT_CLASS =
    'bg-slate-900/50 border border-slate-700/60 text-xs font-mono text-white rounded-lg focus:outline-none focus:border-emerald-500/60 cursor-pointer appearance-none transition-colors';

const SELECT_ARROW_STYLE = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
    backgroundSize: '14px',
};

// Small emerald ghost action used for Clear/Reset controls in team headers.
const GHOST_ACTION_CLASS =
    'px-2 py-1 text-[10px] font-mono font-semibold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded hover:bg-emerald-500/15 hover:border-emerald-500/50 transition-all';

const ROUNDS = Array.from({ length: 25 }, (_, i) => i);

const getPlayerImage = (playerName) => {
    const p = aflPlayerData.find((x) => x.player_name === playerName);
    return p ? p.image_src : null;
};

const TEAM_ABBREVIATIONS = {
    "Adelaide_Crows": "ADL",
    "Brisbane_Lions": "BL", 
    "Carlton": "CARL",
    "Collingwood": "COLL",
    "Essendon": "ESS",
    "Fremantle": "FRE",
    "Geelong_Cats": "GEEL",
    "Gold_Coast_Suns": "GC",
    "GWS_Giants": "GWS",
    "Hawthorn": "HAW",
    "Melbourne": "MELB",
    "North_Melbourne": "NM",
    "Port_Adelaide": "PORT",
    "Richmond": "RICH",
    "St_Kilda": "STK",
    "Sydney_Swans": "SYD",
    "West_Coast_Eagles": "WCE",
    "Western_Bulldogs": "WBD"
};

const TEAM_DISPLAY_NAMES = {
    "Adelaide_Crows": "Adelaide",
    "Brisbane_Lions": "Brisbane", 
    "Carlton": "Carlton",
    "Collingwood": "Collingwood",
    "Essendon": "Essendon",
    "Fremantle": "Fremantle",
    "Geelong_Cats": "Geelong",
    "Gold_Coast_Suns": "Gold Coast",
    "GWS_Giants": "GWS",
    "Hawthorn": "Hawthorn",
    "Melbourne": "Melbourne",
    "North_Melbourne": "North Melbourne",
    "Port_Adelaide": "Port Adelaide",
    "Richmond": "Richmond",
    "St_Kilda": "St Kilda",
    "Sydney_Swans": "Sydney",
    "West_Coast_Eagles": "West Coast",
    "Western_Bulldogs": "Western Bulldogs"
};

const getTeamAbbreviation = (teamName) => {
    return TEAM_ABBREVIATIONS[teamName] || teamName;
};

const getTeamDisplayName = (teamName) => {
    return TEAM_DISPLAY_NAMES[teamName] || teamName;
};

const getTeamLogo = (teamName) => {
    const logos = {
        "Adelaide_Crows": AdlLogo,

        "Brisbane_Lions": BlLogo,
        "Carlton": CarlLogo,
        "Collingwood": CollLogo,
        "Essendon": EssLogo,
        "Fremantle": FreLogo,
        "Geelong_Cats": GeelLogo,
        "Gold_Coast_Suns": GcLogo,
        "GWS_Giants": GwsLogo,
        "Hawthorn": HawLogo,
        "Melbourne": MelbLogo,
        "North_Melbourne": NmLogo,
        "Port_Adelaide": PortLogo,
        "Richmond": RichLogo,
        "St_Kilda": StkLogo,
        "Sydney_Swans": SydLogo,
        "West_Coast_Eagles": WceLogo,
        "Western_Bulldogs": WbdLogo,
    };
    return logos[teamName] || null;
};

const TeamVotesTable = () => {
    const { data: voteDataRemote } = useVoteData();
    const { getEnabledKeys } = useSettings();
    const {
        teamHandicapEnabled,
        playerHandicaps,
        toggleTeamHandicap,
        updateTempHandicapInput,
        applyPlayerHandicap,
        getPlayerHandicap,
        getDisplayHandicapValue,
        resetTeamHandicaps
    } = useHandicap();
    const enabledKeys = getEnabledKeys();
    const enabledSet = new Set(enabledKeys);
    const displayOrder = ["count1","count2","count3","count4","count5","betfair","espn","byb"];
    const enabledDisplayKeys = displayOrder.filter((k) => enabledSet.has(k));
    const keyToLabel = { count1: "BG 1", count2: "BG 2", count3: "BG 3", count4: "BG 4", count5: "BG 5", betfair: "BETFAIR", espn: "ESPN", byb: "BYB" };

    const players = voteDataRemote?.leaderboard || [];
    
    // State for team filters - tracks which players to hide for each team
    const [teamFilters, setTeamFilters] = useState({});
    // State for sorting - tracks sort key for each team
    const [teamSortKeys, setTeamSortKeys] = useState({});
    // State for team modal
    const [selectedTeam, setSelectedTeam] = useState(null);
    const [isTeamModalOpen, setIsTeamModalOpen] = useState(false);
    // State for the player breakdown modal opened from a team's player row
    const [selectedPlayer, setSelectedPlayer] = useState(null);
    const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
    // State for mobile expanded players
    const [expandedMobilePlayers, setExpandedMobilePlayers] = useState({});

    const PLAYER_NAME_TO_TEAM = useMemo(() => {
        const map = new Map();
        for (const p of players) {
            map.set(p.name.toLowerCase(), (p.full_team_name || p.team || "").toString());
        }
        return map;
    }, [players]);

    const buildPerPlayerRoundTotalsFrom = (dataSet) => {
        const result = new Map();
        const data = dataSet && dataSet.game_vote_data ? dataSet.game_vote_data : [];
        for (const roundData of data) {
            const round = String(roundData.round);
            for (const game of roundData.game_votes) {
                for (const vote of game.votes) {
                    const key = vote.player.toLowerCase();
                    if (!PLAYER_NAME_TO_TEAM.has(key)) continue;
                    const expectedTeam = normalize(PLAYER_NAME_TO_TEAM.get(key));
                    if (normalize(vote.team) !== expectedTeam) continue;
                    if (!result.has(key)) result.set(key, {});
                    const r = result.get(key);
                    r[round] = (r[round] || 0) + vote.votes;
                }
            }
        }
        return result;
    };

    const SOURCE_TOTALS = useMemo(() => {
        const wrap = (d) => (Array.isArray(d) ? { game_vote_data: d } : d);
        if (!voteDataRemote) return {
            byb: new Map(), espn: new Map(), betfair: new Map(),
            count1: new Map(), count2: new Map(), count3: new Map(), count4: new Map(), count5: new Map(),
        };
        return {
            byb: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_byb)),
            espn: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_espn)),
            betfair: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_betfair)),
            count1: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_1)),
            count2: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_2)),
            count3: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_3)),
            count4: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_4)),
            count5: buildPerPlayerRoundTotalsFrom(wrap(voteDataRemote.game_vote_data_count_5)),
        };
    }, [voteDataRemote, PLAYER_NAME_TO_TEAM]);

    const getSeasonTotal = (sourceMap, playerName) => {
        const key = playerName.toLowerCase();
        const perRound = sourceMap.get(key) || {};
        let sum = 0;
        for (const r of ROUNDS) sum += Number(perRound[String(r)] || 0);
        return sum;
    };

    const teamToPlayers = useMemo(() => {
        const map = new Map();
        for (const p of players) {
            const team = (p.full_team_name || p.team || "").toString();
            if (!map.has(team)) map.set(team, []);
            map.get(team).push(p);
        }
        return map;
    }, [players]);



    const computeRow = (player, team) => {
        const row = { name: player.name, team: player.team, image: player.image };
        const handicap = getPlayerHandicap(team, player.name);
        const isHandicapEnabled = teamHandicapEnabled[team];
        
        for (const k of ["count1","count2","count3","count4","count5","betfair","espn","byb"]) {
            const m = SOURCE_TOTALS[k];
            const baseValue = getSeasonTotal(m, player.name);
            // Apply handicap only to enabled datasets
            row[k] = baseValue + (isHandicapEnabled && enabledSet.has(k) ? handicap : 0);
        }
        // Average votes over enabled datasets (with handicaps already applied)
        const keys = enabledDisplayKeys;
        const n = keys.length || 1;
        const sum = keys.reduce((acc, k) => acc + Number(row[k] || 0), 0);
        row.votes = n > 0 ? sum / n : 0;
        row.handicap = handicap; // Store handicap for display
        return row;
    };

    const teamTables = useMemo(() => {
        const result = [];
        for (const [team, list] of teamToPlayers.entries()) {
            // Apply team filter to hide specific players
            const hiddenPlayers = teamFilters[team] || [];
            const filteredList = list.filter(player => !hiddenPlayers.includes(player.name));
            
            // Build rows
            const rows = filteredList.map(player => computeRow(player, team));
            
            // Apply sorting based on team's sort key
            const sortKey = teamSortKeys[team] || 'votes';
            rows.sort((a, b) => {
                const va = Number(a[sortKey] || 0);
                const vb = Number(b[sortKey] || 0);
                if (vb !== va) return vb - va; // desc
                // fall back to source leaderboard order using original positions if provided
                const ap = players.find((p) => p.name === a.name);
                const bp = players.find((p) => p.name === b.name);
                return (ap?.position ?? 0) - (bp?.position ?? 0);
            });

            // Per-source team leaders (max within filtered team)
            const teamLeaders = {};
            for (const k of ["count1","count2","count3","count4","count5","betfair","espn","byb"]) {
                const vals = rows.map((r) => r[k] || 0);
                teamLeaders[k] = vals.length ? Math.max(...vals) : 0;
            }

            result.push({ team, rows, teamLeaders, allPlayers: list });
        }
        // Sort teams alphabetically for consistent layout
        result.sort((a, b) => a.team.localeCompare(b.team));
        return result;
    }, [teamToPlayers, enabledDisplayKeys, players, teamFilters, teamSortKeys, teamHandicapEnabled, playerHandicaps, enabledSet]);

    // Implied odds for a player within their team. Dead-heat rules apply: a
    // count where n teammates tie at the top gives each 1/n of a win.
    const renderImpliedCell = (row, teamRows) => {
        const enabled = enabledDisplayKeys;
        const denom = enabled.length || 1;
        let hits = 0;
        for (const k of enabled) {
            const val = Number(row[k] || 0);
            hits += deadHeatShare(val, teamRows.map((r) => r[k] || 0));
        }
        const hitPct = (hits / denom) * 100;
        if (hitPct === 0) return <span className="text-slate-600 text-[11px] font-mono">N/A</span>;
        const odds = (1 / (hitPct / 100)).toFixed(2);
        return (
            <div className="flex flex-col items-center justify-center gap-0.5">
                <span className="text-emerald-300 font-mono font-bold tabular-nums text-xs md:text-[13px]">{odds}</span>
                <span className="text-slate-500 text-[10px] font-mono tabular-nums">{formatWins(hits)}/{denom} ({formatPct(hitPct)}%)</span>
            </div>
        );
    };

    const isTeamLeaderForSource = (playerValue, teamMaxValue) => {
        return teamMaxValue > 0 && playerValue === teamMaxValue;
    };

    const leaderCellClass = (flag) => (flag ? 'bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 rounded-md' : '');

    // Filter functions
    const togglePlayerFilter = (team, playerName) => {
        setTeamFilters(prev => {
            const teamHidden = prev[team] || [];
            const isCurrentlyHidden = teamHidden.includes(playerName);
            
            if (isCurrentlyHidden) {
                // Remove from hidden list
                return {
                    ...prev,
                    [team]: teamHidden.filter(name => name !== playerName)
                };
            } else {
                // Add to hidden list
                return {
                    ...prev,
                    [team]: [...teamHidden, playerName]
                };
            }
        });
    };

    const clearTeamFilter = (team) => {
        setTeamFilters(prev => ({
            ...prev,
            [team]: []
        }));
    };

    // Mobile player rows default to expanded; toggling stores an explicit override.
    const toggleMobilePlayer = (playerName) => {
        setExpandedMobilePlayers((prev) => ({ ...prev, [playerName]: !(prev[playerName] ?? true) }));
    };

    const handleSort = (team, sortKey) => {
        setTeamSortKeys(prev => ({
            ...prev,
            [team]: sortKey
        }));
    };

    // The players a team's figures are built from: its squad minus anyone the
    // team filter is hiding. The modal aggregates the same list so its charts
    // agree with the totals below.
    const getTeamRoster = (teamName) => {
        const teamPlayers = teamToPlayers.get(teamName) || [];
        const hiddenPlayers = teamFilters[teamName] || [];
        return teamPlayers.filter(player => !hiddenPlayers.includes(player.name));
    };

    // Calculate team totals for modal
    const calculateTeamTotals = (teamName) => {
        const filteredPlayers = getTeamRoster(teamName);

        const totals = { team: teamName };
        for (const k of ["count1","count2","count3","count4","count5","betfair","espn","byb"]) {
            const sourceMap = SOURCE_TOTALS[k];
            let teamTotal = 0;
            for (const player of filteredPlayers) {
                teamTotal += getSeasonTotal(sourceMap, player.name);
            }
            totals[k] = teamTotal;
        }
        return totals;
    };

    // Modal handlers
    const openTeamModal = (teamName) => {
        setSelectedTeam(teamName);
        setIsTeamModalOpen(true);
    };

    const closeTeamModal = () => {
        setSelectedTeam(null);
        setIsTeamModalOpen(false);
    };

    // A player row opens the same breakdown modal as the leaderboard, from the
    // leaderboard record so the modal has the fields it expects.
    const openPlayerModal = (playerName, team) => {
        const source = players.find((p) => p.name === playerName);
        if (!source) return;
        setSelectedPlayer({ ...source, full_team_name: source.full_team_name || team });
        setIsPlayerModalOpen(true);
    };

    const closePlayerModal = () => {
        setSelectedPlayer(null);
        setIsPlayerModalOpen(false);
    };

    if (!players.length) {
        return (
            <div className="bg-[#0a1120] rounded-2xl border border-slate-800 p-4 text-slate-400 text-sm">No data</div>
        );
    }



    return (
        <div>
            {/* Mobile: Team tables with simplified player rows (PLAYER, VOTES) with expandable rows */}
            <div className="lg:hidden space-y-4">
                {teamTables.map(({ team, rows, teamLeaders, allPlayers }) => {
                    const hiddenPlayers = teamFilters[team] || [];
                    
                    return (
                        <div key={`mobile-${team}`} id={`mobile-team-${team.replace(/\s+/g, '-').toLowerCase()}`} className="bg-[#0a1120] rounded-2xl border border-slate-800 overflow-hidden scroll-mt-28">
                            {/* Team Header */}
                            <div className="px-4 py-3 border-b border-slate-800">
                                {/* Team Logo/Name - First Line - Centered */}
                                <div 
                                    className="flex items-center justify-center gap-3 text-white font-semibold text-sm hover:text-emerald-300 cursor-pointer transition-colors mb-2"
                                    onClick={() => openTeamModal(team)}
                                    title="View team vote breakdown"
                                >
                                    {getTeamLogo(team) && (
                                        <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                            <img 
                                                src={getTeamLogo(team)} 
                                                alt={`${team} logo`}
                                                className="w-5 h-5 object-contain"
                                            />
                                        </div>
                                    )}
                                    <span>{getTeamDisplayName(team)}</span>
                                    <BreakdownIcon className="w-3.5 h-3.5 flex-shrink-0 text-emerald-400/60" />
                                </div>

                                {/* Controls - Second Line - W/O Left, Handicap Right */}
                                <div className="flex items-center justify-between">
                                    {/* W/O Player Filter - Left Side */}
                                    <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-slate-500">W/O</span>
                                        <select
                                            className={`${SELECT_CLASS} px-2 py-1 pr-7 min-w-0 max-w-[100px]`}
                                            style={SELECT_ARROW_STYLE}
                                            value=""
                                            onChange={(e) => {
                                                if (e.target.value) {
                                                    togglePlayerFilter(team, e.target.value);
                                                    e.target.value = "";
                                                }
                                            }}
                                        >
                                            <option value="">Player...</option>
                                            {allPlayers
                                                .filter(p => !hiddenPlayers.includes(p.name))
                                                .map(p => (
                                                    <option key={p.name} value={p.name}>{p.name}</option>
                                                ))
                                            }
                                        </select>
                                        {hiddenPlayers.length > 0 && (
                                            <button
                                                onClick={() => clearTeamFilter(team)}
                                                className={GHOST_ACTION_CLASS}
                                            >
                                                Clear ({hiddenPlayers.length})
                                            </button>
                                        )}
                                    </div>
                                    
                                    {/* Handicap Toggle - Right Side */}
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-slate-500">Handicap</span>
                                        <button
                                            onClick={() => toggleTeamHandicap(team)}
                                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
                                                teamHandicapEnabled[team] 
                                                    ? 'bg-emerald-500' 
                                                    : 'bg-slate-700'
                                            }`}
                                        >
                                            <span
                                                className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
                                                    teamHandicapEnabled[team] ? 'translate-x-5' : 'translate-x-1'
                                                }`}
                                            />
                                        </button>
                                        {teamHandicapEnabled[team] && (
                                            <button
                                                onClick={() => resetTeamHandicaps(team)}
                                                className={GHOST_ACTION_CLASS}
                                                title="Reset all handicaps for this team"
                                            >
                                                Reset
                                            </button>
                                        )}
                                    </div>
                                </div>
                                {hiddenPlayers.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                        {hiddenPlayers.map(playerName => (
                                            <span
                                                key={playerName}
                                                className="inline-flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] font-mono px-2 py-1 rounded"
                                            >
                                                W/O {playerName}
                                                <button
                                                    onClick={() => togglePlayerFilter(team, playerName)}
                                                    className="text-emerald-400 hover:text-emerald-300 ml-1"
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            
                            {/* Mobile Player Table */}
                            <div className="overflow-hidden">
                                <table className="w-full table-fixed">
                                    <colgroup>
                                        {teamHandicapEnabled[team] ? (
                                            <>
                                                <col className="w-[50%]" />
                                                <col className="w-[25%]" />
                                                <col className="w-[25%]" />
                                            </>
                                        ) : (
                                            <>
                                                <col className="w-[70%]" />
                                                <col className="w-[30%]" />
                                            </>
                                        )}
                                    </colgroup>
                                    <thead>
                                        <tr className="bg-slate-800/20 border-b border-slate-800/80">
                                            <th className={`text-center pl-2 pr-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] ${teamHandicapEnabled[team] ? 'w-[50%]' : 'w-[70%]'}`}>Player</th>
                                            {teamHandicapEnabled[team] && (
                                                <th className="w-[25%] text-center px-1 py-2.5 text-[10px] font-mono font-medium text-sky-400 uppercase tracking-[0.12em]">Handicap</th>
                                            )}
                                            <th className={`text-center pl-2 pr-4 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] bg-emerald-500/10 ${teamHandicapEnabled[team] ? 'w-[25%]' : 'w-[30%]'}`}>Votes</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => {
                                            const img = getPlayerImage(row.name);
                                            const isExpanded = expandedMobilePlayers[row.name] ?? true;
                                            
                                            return (
                                                <React.Fragment key={`mobile-${team}-${row.name}`}>
                                                    <tr className="border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors">
                                                        <td className={`pl-2 pr-2 py-2 align-top ${teamHandicapEnabled[team] ? 'w-[50%]' : 'w-[70%]'}`}>
                                                            <div className="flex items-center space-x-2 min-w-0">
                                                                {img ? (
                                                                    <img src={img} alt={row.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60" />
                                                                ) : (
                                                                    <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                                                        <span className="text-white font-bold text-xs">{row.image}</span>
                                                                    </div>
                                                                )}
                                                                <div className="min-w-0 flex-1">
                                                                    <div
                                                                        className="flex items-center gap-1.5 min-w-0 text-white font-semibold text-xs cursor-pointer hover:text-emerald-300 transition-colors"
                                                                        onClick={() => openPlayerModal(row.name, team)}
                                                                        title="View vote breakdown"
                                                                    >
                                                                        <span className="truncate">{row.name}</span>
                                                                        <BreakdownIcon className="w-3 h-3 flex-shrink-0 text-emerald-400/60" />
                                                                    </div>
                                                                    <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{row.team}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        {teamHandicapEnabled[team] && (
                                                            <td className="w-[25%] px-1 py-2 align-top text-center">
                                                                <input
                                                                    type="number"
                                                                    step="0.5"
                                                                    min="0"
                                                                    placeholder="0.0"
                                                                    value={getDisplayHandicapValue(team, row.name)}
                                                                    onChange={(e) => updateTempHandicapInput(team, row.name, e.target.value)}
                                                                    onBlur={(e) => applyPlayerHandicap(team, row.name, e.target.value)}
                                                                    className="w-16 bg-slate-950/60 border border-slate-700/60 text-white font-mono tabular-nums rounded-md text-xs px-1 py-1 text-center focus:outline-none focus:border-emerald-500/60 transition-colors"
                                                                />
                                                            </td>
                                                        )}
                                                        <td className={`pl-2 pr-4 py-2 align-top text-right bg-emerald-500/[0.06] ${teamHandicapEnabled[team] ? 'w-[25%]' : 'w-[30%]'}`}>
                                                            <div className="inline-flex items-center justify-end gap-2 pr-1">
                                                                <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm">{row.votes.toFixed(2)}</span>
                                                                <button
                                                                    className={`w-7 h-7 flex items-center justify-center rounded-md border cursor-pointer transition-all duration-200 ${isExpanded ? 'rotate-180 bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'border-slate-700/60 text-slate-400 hover:text-slate-200 hover:border-slate-600'}`}
                                                                    aria-label={isExpanded ? 'Collapse player' : 'Expand player'}
                                                                    onClick={() => toggleMobilePlayer(row.name)}
                                                                >
                                                                    <ChevronDown />
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr className="bg-[#060b16] border-b border-slate-800/50">
                                                            <td colSpan={teamHandicapEnabled[team] ? 3 : 2} className="px-0 pb-0">
                                                                <div className="overflow-x-auto">
                                                                    <table className="min-w-[720px] w-full">
                                                                        <thead>
                                                                            <tr className="bg-slate-800/20">
                                                                                {enabledDisplayKeys.map((k) => (
                                                                                    <th key={`mh-${team}-${row.name}-${k}`} className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap">{keyToLabel[k]}</th>
                                                                                ))}
                                                                                <th className="text-center px-2 py-2 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] whitespace-nowrap"><span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span></th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            <tr>
                                                                                {enabledDisplayKeys.map((k) => (
                                                                                    <td key={`mv-${team}-${row.name}-${k}`} className={`text-center px-2 py-2 whitespace-nowrap ${leaderCellClass(isTeamLeaderForSource(row[k] || 0, teamLeaders[k]))}`}>
                                                                                        <div className="flex flex-col items-center">
                                                                                            <span className="text-slate-400 font-mono tabular-nums text-xs">{(row[k] || 0).toFixed(2)}</span>
                                                                                            {isTeamLeaderForSource(row[k] || 0, teamLeaders[k]) && (
                                                                                                <LeaderCheck />
                                                                                            )}
                                                                                        </div>
                                                                                    </td>
                                                                                ))}
                                                                                <td className="text-center px-2 py-2 whitespace-nowrap">
                                                                                    {renderImpliedCell(row, rows)}
                                                                                </td>
                                                                            </tr>
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Desktop: Team Tables */}
            <div className="hidden lg:block space-y-6">
                {teamTables.map(({ team, rows, teamLeaders, allPlayers }) => {
                const hiddenPlayers = teamFilters[team] || [];
                return (
                <div key={team} id={`team-${team.replace(/\s+/g, '-').toLowerCase()}`} className="bg-[#0a1120] rounded-2xl border border-slate-800 overflow-hidden scroll-mt-16">
                    <div className="px-4 py-3 border-b border-slate-800">
                                                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                            <div 
                                className="flex items-center gap-3 text-white font-semibold text-sm hover:text-emerald-300 cursor-pointer transition-colors"
                                onClick={() => openTeamModal(team)}
                                title="View team vote breakdown"
                            >
                                {getTeamLogo(team) && (
                                    <div className="w-9 h-9 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                        <img 
                                            src={getTeamLogo(team)} 
                                            alt={`${team} logo`}
                                            className="w-6 h-6 object-contain"
                                        />
                                    </div>
                                )}
                                <span>{getTeamDisplayName(team)}</span>
                                <BreakdownIcon className="w-3.5 h-3.5 flex-shrink-0 text-emerald-400/60" />
        </div>
                            <div className="flex items-center gap-3">
                                <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-slate-500">W/O Player</span>
                                <select
                                    className={`${SELECT_CLASS} px-3 h-9 pr-8`}
                                    style={SELECT_ARROW_STYLE}
                                    value=""
                                    onChange={(e) => {
                                        if (e.target.value) {
                                            togglePlayerFilter(team, e.target.value);
                                            e.target.value = "";
                                        }
                                    }}
                                >
                                    <option value="">Player...</option>
                                    {allPlayers
                                        .filter(p => !hiddenPlayers.includes(p.name))
                                        .map(p => (
                                            <option key={p.name} value={p.name}>{p.name}</option>
                                        ))
                                    }
                                </select>
                                {hiddenPlayers.length > 0 && (
                                    <button
                                        onClick={() => clearTeamFilter(team)}
                                        className={GHOST_ACTION_CLASS}
                                    >
                                        Clear ({hiddenPlayers.length})
                                    </button>
                                )}
                                
                                {/* Handicap Toggle */}
                                <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-slate-500">Handicap</span>
                                    <button
                                        onClick={() => toggleTeamHandicap(team)}
                                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500/60 focus:ring-offset-2 focus:ring-offset-slate-900 ${
                                            teamHandicapEnabled[team] 
                                                ? 'bg-emerald-500' 
                                                : 'bg-slate-700'
                                        }`}
                                    >
                                        <span
                                            className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
                                                teamHandicapEnabled[team] ? 'translate-x-5' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                    {teamHandicapEnabled[team] && (
                                        <button
                                            onClick={() => resetTeamHandicaps(team)}
                                            className={GHOST_ACTION_CLASS}
                                            title="Reset all handicaps for this team"
                                        >
                                            Reset
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        {hiddenPlayers.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                                {hiddenPlayers.map(playerName => (
                                    <span
                                        key={playerName}
                                        className="inline-flex items-center gap-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-[11px] font-mono px-2 py-1 rounded"
                                    >
                                        W/O {playerName}
                                        <button
                                            onClick={() => togglePlayerFilter(team, playerName)}
                                            className="text-emerald-400 hover:text-emerald-300 ml-1"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full md:min-w-[900px]">
                            <thead>
                                <tr className="bg-slate-800/20 border-b border-slate-800/80">
                                    <th className="text-left px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] min-w-[200px]">Player</th>
                                    {enabledDisplayKeys.map((k) => {
                                        const currentSortKey = teamSortKeys[team] || 'votes';
                                        return (
                                            <th 
                                                key={`h-${team}-${k}`} 
                                                onClick={() => handleSort(team, k)}
                                                className="text-center px-1 md:px-2 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em] cursor-pointer select-none hover:text-slate-300 transition-colors"
                                            >
                                                {keyToLabel[k]} {currentSortKey === k && <SortCaret />}
                                            </th>
                                        );
                                    })}
                                    {teamHandicapEnabled[team] && (
                                        <th className="text-center px-2 py-2.5 text-[10px] font-mono font-medium text-sky-400 uppercase tracking-[0.12em] min-w-[100px]">Handicap</th>
                                    )}
                                    <th 
                                        onClick={() => handleSort(team, 'votes')}
                                        className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-emerald-300 uppercase tracking-[0.12em] cursor-pointer select-none bg-emerald-500/10 hover:text-emerald-200 transition-colors"
                                    >
                                        Votes {(teamSortKeys[team] || 'votes') === 'votes' && <SortCaret />}
                                    </th>
                                    <th className="text-center px-2 md:px-4 py-2.5 text-[10px] font-mono font-medium text-slate-500 uppercase tracking-[0.12em]"><span className="inline-flex items-center justify-center">Implied Odds<ImpliedOddsInfo /></span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => {
                                    const img = getPlayerImage(row.name);
                                    return (
                                        <tr key={`${team}-${row.name}`} className="border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors">
                                            <td className="px-2 md:px-4 py-1.5 min-w-[200px]">
                                                <div className="flex items-center space-x-2 md:space-x-3">
                                                    {img ? (
                                                        <img src={img} alt={row.name} className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover flex-shrink-0 ring-1 ring-slate-700/60" />
                                                    ) : (
                                                        <div className="w-8 h-8 md:w-9 md:h-9 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 ring-1 ring-slate-700/60">
                                                            <span className="text-white font-bold text-xs">{row.image}</span>
                                                        </div>
                                                    )}
                                                    <div className="min-w-0 flex-1">
                                                        <div
                                                            className="flex items-center gap-1.5 min-w-0 text-white font-semibold text-xs md:text-[13px] cursor-pointer hover:text-emerald-300 transition-colors"
                                                            onClick={() => openPlayerModal(row.name, team)}
                                                            title="View vote breakdown"
                                                        >
                                                            <span className="truncate">{row.name}</span>
                                                            <BreakdownIcon className="w-3 h-3 flex-shrink-0 text-emerald-400/60" />
                                                        </div>
                                                        <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-slate-500">{row.team}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            {enabledDisplayKeys.map((k) => (
                                                <td key={`v-${team}-${row.name}-${k}`} className={`px-1 md:px-2 py-1.5 text-center ${leaderCellClass(isTeamLeaderForSource(row[k] || 0, teamLeaders[k]))}`}>
                                                    <div className="flex flex-col items-center">
                                                        <span className="text-slate-400 font-mono tabular-nums text-xs md:text-[13px]">{(row[k] || 0).toFixed(2)}</span>
                                                        {isTeamLeaderForSource(row[k] || 0, teamLeaders[k]) && (
                                                            <LeaderCheck />
                                                        )}
                                                    </div>
                                                </td>
                                            ))}
                                            {teamHandicapEnabled[team] && (
                                                <td className="px-2 py-1.5 text-center">
                                                    <input
                                                        type="number"
                                                        step="0.5"
                                                        min="0"
                                                        placeholder="0.0"
                                                        value={getDisplayHandicapValue(team, row.name)}
                                                        onChange={(e) => updateTempHandicapInput(team, row.name, e.target.value)}
                                                        onBlur={(e) => applyPlayerHandicap(team, row.name, e.target.value)}
                                                        className="w-16 bg-slate-950/60 border border-slate-700/60 text-white font-mono tabular-nums rounded-md text-xs px-2 py-1 text-center focus:outline-none focus:border-emerald-500/60 transition-colors"
                                                    />
                                                </td>
                                            )}
                                            <td className="px-2 md:px-4 py-1.5 text-center bg-emerald-500/[0.06]">
                                                <span className="text-emerald-300 font-mono font-bold tabular-nums text-sm md:text-[15px]">{row.votes.toFixed(2)}</span>
                                            </td>
                                            <td className="px-2 md:px-4 py-1.5 text-center">
                                                {renderImpliedCell(row, rows)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                );
            })}
            </div>

            {/* Team Details Modal */}
            <DetailedTeamVotesModal
                isOpen={isTeamModalOpen}
                onClose={closeTeamModal}
                team={selectedTeam}
                teamData={selectedTeam ? getTeamRoster(selectedTeam) : null}
                teamTotals={selectedTeam ? calculateTeamTotals(selectedTeam) : null}
            />

            {/* Player Details Modal */}
            <DetailedPlayerVoteModal
                isOpen={isPlayerModalOpen}
                onClose={closePlayerModal}
                player={selectedPlayer}
                playerImage={selectedPlayer ? getPlayerImage(selectedPlayer.name) : null}
                gameVoteData={voteDataRemote || null}
            />
        </div>
    );
};

export default TeamVotesTable;